import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  hasConsented,
  getPreferences,
  saveConsent,
  acceptAll,
  rejectAll,
  type CookiePreferences
} from '@/lib/cookies'
import { cn } from '@/lib/utils'

export function CookieConsent() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(getPreferences())

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasConsented()) {
        setVisible(true)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  const handleAcceptAll = () => {
    acceptAll()
    setVisible(false)
  }

  const handleRejectAll = () => {
    rejectAll()
    setVisible(false)
  }

  const handleSavePreferences = () => {
    saveConsent(preferences)
    setVisible(false)
    setShowSettings(false)
  }

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
  }

  if (!visible) return null

  return (
    <>
      {/* Backdrop for settings modal */}
      {showSettings && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-2xl">
          <h3 className="font-display text-xl font-semibold text-neutral-900">
            {t('cookies.customize')}
          </h3>
          <p className="mt-2 text-body-sm text-neutral-600">
            {t('cookies.description')}
          </p>

          <div className="mt-6 space-y-4">
            {/* Necessary */}
            <div className="flex items-center justify-between rounded-lg bg-neutral-50 p-4">
              <div>
                <p className="font-medium text-neutral-900">{t('cookies.necessary')}</p>
                <p className="text-body-xs text-neutral-500">{t('cookies.necessaryDesc')}</p>
              </div>
              <div className="h-6 w-11 rounded-full bg-accent-500 p-0.5">
                <div className="h-5 w-5 translate-x-5 rounded-full bg-white shadow" />
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between rounded-lg bg-neutral-50 p-4">
              <div>
                <p className="font-medium text-neutral-900">{t('cookies.analytics')}</p>
                <p className="text-body-xs text-neutral-500">{t('cookies.analyticsDesc')}</p>
              </div>
              <button
                onClick={() => togglePreference('analytics')}
                className={cn(
                  "h-6 w-11 rounded-full p-0.5 transition-colors",
                  preferences.analytics ? "bg-accent-500" : "bg-neutral-300"
                )}
                role="switch"
                aria-checked={preferences.analytics}
              >
                <div className={cn(
                  "h-5 w-5 rounded-full bg-white shadow transition-transform",
                  preferences.analytics && "translate-x-5"
                )} />
              </button>
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between rounded-lg bg-neutral-50 p-4">
              <div>
                <p className="font-medium text-neutral-900">{t('cookies.marketing')}</p>
                <p className="text-body-xs text-neutral-500">{t('cookies.marketingDesc')}</p>
              </div>
              <button
                onClick={() => togglePreference('marketing')}
                className={cn(
                  "h-6 w-11 rounded-full p-0.5 transition-colors",
                  preferences.marketing ? "bg-accent-500" : "bg-neutral-300"
                )}
                role="switch"
                aria-checked={preferences.marketing}
              >
                <div className={cn(
                  "h-5 w-5 rounded-full bg-white shadow transition-transform",
                  preferences.marketing && "translate-x-5"
                )} />
              </button>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={() => setShowSettings(false)}
              className="flex-1 rounded-brand border border-neutral-300 px-4 py-2.5 text-body-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePreferences}
              className="flex-1 rounded-brand bg-accent-500 px-4 py-2.5 text-body-sm font-medium text-white transition-colors hover:bg-accent-400"
            >
              {t('cookies.savePreferences')}
            </button>
          </div>
        </div>
      )}

      {/* Bottom Banner */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white shadow-lg">
        <div className="container-lg flex flex-col items-center gap-4 py-4 sm:flex-row sm:justify-between">
          <div className="flex-1 text-center sm:text-left">
            <p className="text-body-sm text-neutral-700">
              {t('cookies.description')}{' '}
              <Link to="/privacy" className="text-accent-600 underline hover:text-accent-500">
                {t('cookies.privacyPolicy')}
              </Link>
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => setShowSettings(true)}
              className="rounded-brand border border-neutral-300 px-4 py-2 text-body-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              {t('cookies.customize')}
            </button>
            <button
              onClick={handleRejectAll}
              className="rounded-brand border border-neutral-300 px-4 py-2 text-body-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              {t('cookies.rejectAll')}
            </button>
            <button
              onClick={handleAcceptAll}
              className="rounded-brand bg-accent-500 px-4 py-2 text-body-sm font-medium text-white transition-colors hover:bg-accent-400"
            >
              {t('cookies.acceptAll')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default CookieConsent
